import type { Ref } from 'react'
import { FileTextOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { pluralize } from '../../lib/format/pluralize'
import { formatMoney } from '../cart/cart-money'
import type { CheckoutView } from './checkout-domain'

type CheckoutSummary = CheckoutView['summary']

export interface CheckoutSummaryRailProps {
  readonly summary: CheckoutSummary
  readonly termsAccepted: boolean
  readonly onTermsChange: (checked: boolean) => void
  readonly termsCheckboxRef: Ref<HTMLInputElement>
  readonly onPlaceOrder: () => void
  readonly submitting: boolean
  /** Opens the shared `GenerateQuoteModal` (UID-139) — the modal owns the whole
   * confirm → generate → ready flow, so the rail just triggers it. */
  readonly onGenerateQuote: () => void
  /** True only while the cart is being fetched to open the modal (button spinner). */
  readonly generatingQuote: boolean
  /** "Place order" / "Place N orders" (UID-97 K1, `placeOrderCtaLabel`). */
  readonly placeOrderLabel: string
  /** UID-97 K1 split-PO — the cart's ready lines partition into more than one PO
   * group. Kept so the rail knows a split is structurally possible; the CHOICE itself
   * now lives on the cart (UID-315, ADR-0106) — checkout is choice-driven, no toggle. */
  readonly canSplit: boolean
  /** "Placing N purchase orders" (prototype `co.splitNote`, line 2132) — present only
   * when the buyer chose split on the cart AND `canSplit` (its presence encodes the
   * active split, UID-315). Also drives the choice-driven callout below. */
  readonly splitNote?: string | undefined
  /** UID-269 — "N item(s) ship now · M item(s) stay in your cart" for a mixed
   * cart (`describeCheckoutSplit`, `cart-readiness.ts`). This checkout is
   * placing only the ready lines; any pending/quote-required lines are left
   * bound to the cart and append to it on acceptance. `undefined` when there
   * is nothing pending to call out (an all-ready cart). */
  readonly readinessSplitNote?: string | undefined
}

/**
 * The single-page checkout's sticky order-summary rail (Stage-2b): the
 * server-authoritative four-line total (`CheckoutView.summary`, Stage-2a,
 * ADR-0029 — every figure here is read directly off `summary`, never
 * re-derived client-side), the terms checkbox with the exact approved copy,
 * a primary "Place order" CTA, and a secondary "Generate quote" CTA that opens
 * the shared `GenerateQuoteModal` (UID-139), matching the prototype where
 * checkout's `co.onGenerateQuote` calls the same `openQuote()` as the cart.
 *
 * Place order uses `aria-disabled` (never native `disabled`) while terms are
 * unchecked: the button stays focusable/discoverable, and clicking, Enter,
 * or Space while unchecked moves focus to the terms checkbox and announces
 * why instead of submitting — see `onPlaceOrder`'s caller (`CheckoutPage`)
 * for that gating logic, which also runs the payment/shipping selection
 * validation on click (focus-first-invalid-field, UX spec §3.3). Native
 * `disabled` IS correct while a real placement request is in flight
 * (`submitting`) — that is the one case a truly inert control is right, to
 * prevent a double-submit.
 */
export function CheckoutSummaryRail({
  summary,
  termsAccepted,
  onTermsChange,
  termsCheckboxRef,
  onPlaceOrder,
  submitting,
  onGenerateQuote,
  generatingQuote,
  placeOrderLabel,
  canSplit,
  splitNote,
  readinessSplitNote,
}: CheckoutSummaryRailProps) {
  const termsCaptionId = 'checkout-terms-caption'

  return (
    <Card aria-labelledby="checkout-summary-heading" className="checkout-summary-rail">
      <h2 id="checkout-summary-heading" className="heading-text">
        Order summary
      </h2>
      {/* UID-269 — checkout splits by readiness: this checkout places only the
          ready lines; a mixed cart's pending/quote-required lines stay bound
          to the cart and append to it on acceptance. Informational, never a
          block — placement below always proceeds with just the ready lines. */}
      {readinessSplitNote && <p className="checkout-readiness-split-note">{readinessSplitNote}</p>}
      <dl className="cart-summary-totals">
        <dt>
          Subtotal ({summary.itemCount} {pluralize(summary.itemCount, 'item')})
        </dt>
        <dd>{formatMoney(summary.subtotalAmount, summary.currency)}</dd>
        <dt>Shipping</dt>
        <dd>{formatMoney(summary.shippingAmount, summary.currency)}</dd>
        <dt>Tax</dt>
        <dd>{formatMoney(summary.taxAmount, summary.currency)}</dd>
        <dt className="cart-summary-total-label">Total</dt>
        <dd className="cart-summary-total-value">
          {formatMoney(summary.totalAmount, summary.currency)}
        </dd>
      </dl>

      {/* UID-315 (ADR-0106) — checkout is CHOICE-DRIVEN: the "Separate POs per group"
          choice was made on the CART, so there is NO toggle here (superseding the
          ADR-0044 Switch). When the buyer chose split AND the ready lines form 2+ groups,
          `splitNote` ("Placing N purchase orders") is present — show the static choice
          callout + note. Otherwise checkout places a single PO and nothing shows here. */}
      {splitNote && (
        <div className="checkout-split-po-row">
          <div className="checkout-split-po-copy">
            <span className="checkout-split-po-title">Separate POs per group</span>
            <span className="caption-text">
              You chose to place separate POs per group in your cart. Enter a PO number for each.
            </span>
          </div>
        </div>
      )}
      {splitNote && <p className="checkout-split-po-note">{splitNote}</p>}
      {/* Screens.md "price-recalculating (flash)" state: a sighted user sees
          the total update when a §1/§2 selection changes; this POLITE live
          region announces the new total to assistive tech (WCAG F4). */}
      <p className="sr-only" aria-live="polite">
        Order total updated: {formatMoney(summary.totalAmount, summary.currency)}
      </p>

      <div className="checkout-terms-row">
        <input
          ref={termsCheckboxRef}
          id="checkout-terms-checkbox"
          type="checkbox"
          className="checkbox-input"
          checked={termsAccepted}
          onChange={(event) => onTermsChange(event.target.checked)}
          aria-describedby={!termsAccepted ? termsCaptionId : undefined}
        />
        <label htmlFor="checkout-terms-checkbox" className="checkbox-label-text">
          I agree to Fictiv&rsquo;s{' '}
          <a href="/terms" onClick={(event) => event.stopPropagation()}>
            Terms of Service
          </a>{' '}
          and confirm the parts and requirements above.
        </label>
      </div>
      {/* Design (Fictiv Platform.dc.html:2133-2134) shows no visible caption
          between the terms checkbox and the CTA — the dimmed (opacity-0.55)
          button is the visual "can't place yet" cue. Kept as `sr-only` so the
          button's `aria-describedby` still announces WHY it's disabled to
          assistive tech, without adding a line the design doesn't have. */}
      {!termsAccepted && (
        <p id={termsCaptionId} className="sr-only">
          Accept the terms above to place your order.
        </p>
      )}

      <Button
        variant="primary"
        size="lg"
        loading={submitting}
        aria-disabled={!termsAccepted || undefined}
        aria-describedby={!termsAccepted ? termsCaptionId : undefined}
        onClick={onPlaceOrder}
        className="checkout-place-order-btn"
      >
        {placeOrderLabel}
      </Button>

      {/* UID-431: match the cart's "Generate quote" CTA exactly (variant + the shared
          `cart-summary-secondary-btn` treatment) so the button reads identically on both
          the cart and the checkout order-summary rails. */}
      <Button
        variant="secondary"
        size="lg"
        loading={generatingQuote}
        onClick={onGenerateQuote}
        className="cart-summary-secondary-btn"
      >
        <FileTextOutlined aria-hidden="true" />
        Generate quote
      </Button>
    </Card>
  )
}
